import { useEffect, useId, useLayoutEffect, useRef, useState, type ReactNode } from 'react';

interface ActionMenuProps {
  label: string;
  trigger: ReactNode;
  triggerClassName: string;
  children: (close: () => void) => ReactNode;
}

/** A top-layer menu stays inside its focus scope without being clipped by scroll containers. */
export function ActionMenu({ label, trigger, triggerClassName, children }: ActionMenuProps) {
  const [open, setOpen] = useState(false);
  const [position, setPosition] = useState<{ left: number; top: number } | null>(null);
  const id = useId();
  const wrapRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);

  const close = () => {
    menuRef.current?.hidePopover();
    buttonRef.current?.focus();
    setOpen(false);
  };

  useEffect(() => {
    if (!open) return;
    const dismissOutside = (event: PointerEvent) => {
      if (!wrapRef.current?.contains(event.target as Node)) setOpen(false);
    };
    const dismissWithEscape = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return;
      event.preventDefault();
      event.stopPropagation();
      menuRef.current?.hidePopover();
      buttonRef.current?.focus();
      setOpen(false);
    };
    document.addEventListener('pointerdown', dismissOutside);
    document.addEventListener('keydown', dismissWithEscape);
    return () => {
      document.removeEventListener('pointerdown', dismissOutside);
      document.removeEventListener('keydown', dismissWithEscape);
    };
  }, [open]);

  useLayoutEffect(() => {
    if (!open) return;
    const menu = menuRef.current;
    menu?.showPopover();
    const updatePosition = () => {
      const trigger = buttonRef.current;
      if (!trigger || !menu) return;
      const rect = trigger.getBoundingClientRect();
      const gap = 6;
      const margin = 8;
      const width = menu.offsetWidth;
      const height = menu.offsetHeight;
      const maxLeft = Math.max(margin, window.innerWidth - width - margin);
      const left = Math.max(margin, Math.min(rect.right - width, maxLeft));
      const opensUp = window.innerHeight - rect.bottom < height + gap + margin
        && rect.top >= height + gap + margin;
      const top = opensUp ? rect.top - height - gap
        : Math.min(rect.bottom + gap, window.innerHeight - height - margin);
      setPosition({ left, top: Math.max(margin, top) });
    };
    updatePosition();
    window.addEventListener('resize', updatePosition);
    document.addEventListener('scroll', updatePosition, true);
    return () => {
      if (menu?.isConnected) menu.hidePopover();
      window.removeEventListener('resize', updatePosition);
      document.removeEventListener('scroll', updatePosition, true);
    };
  }, [open]);

  return <div className="menu-wrap" ref={wrapRef} data-modal-escape-boundary={open ? '' : undefined}
    onBlur={event => { if (event.relatedTarget && !event.currentTarget.contains(event.relatedTarget)) setOpen(false); }}>
    <button ref={buttonRef} type="button" className={triggerClassName} aria-label={label} aria-expanded={open} aria-controls={open ? id : undefined} onClick={() => { setPosition(null); setOpen(value => !value); }}>{trigger}</button>
    {open && <div id={id} ref={menuRef} popover="manual" role="group" aria-label={label} className="context-menu context-menu--floating"
      style={position ? { left: position.left, top: position.top } : { left: 0, top: 0, visibility: 'hidden' }}>
      {children(close)}
    </div>}
  </div>;
}
